import type { ReactNode } from "react";
import "./radio-group.css";

export type RadioOption<V extends string = string> = {
  value: V;
  label: ReactNode;
  /** Una riga di spiegazione sotto la scelta, legata al suo pallino. */
  hint?: ReactNode;
  /** Qualcosa accanto al nome sulla stessa riga (un `Chip`, «arriva con il prossimo rilascio»): non fa parte del
      nome del pallino, ma lo descrive. */
  aside?: ReactNode;
  disabled?: boolean;
};

type Props<V extends string> = {
  /** Il `name` dei pallini e la base dei loro `id` (`${name}-${indice}`): unico nella pagina. */
  name: string;
  /** Il titolo del gruppo (`<legend>`): è il nome del gruppo per gli screen reader. */
  legend: ReactNode;
  /** Il titolo resta per gli screen reader ma non si vede (lo dice già il titolo della sezione). */
  hideLegend?: boolean;
  options: readonly RadioOption<V>[];
  /** Un controllo che sta in testa alla griglia delle scelte, prima dei pallini, e ne prende l'aspetto (in `tiles`):
      un'azione che si sceglie insieme alle scelte, come «Richiama subito» fra gli esiti. Non è una scelta del gruppo. */
  leading?: ReactNode;
  /** `null`: nessuna scelta ancora fatta. */
  value: V | null;
  onChange: (value: V) => void;
  /** Spegne tutto il gruppo; il motivo va scritto altrove e legato con `describedBy`. */
  disabled?: boolean;
  /** L'`id` di un testo scritto fuori dal gruppo (il motivo per cui è spento, una nota sotto). */
  describedBy?: string;
  /** `list`: pallini uno sotto l'altro (Impostazioni). `tiles`: riquadri in griglia, tutto il riquadro si tocca (esito della chiamata).
      `cards`: una scheda larga per scelta, con nome, `aside` e spiegazione dentro lo stesso bordo (il canale WhatsApp). */
  look?: "list" | "tiles" | "cards";
  className?: string;
  /** Quello che appartiene al gruppo ma non è una scelta, sotto le scelte: un campo legato all'ultima scelta (il Commerciale), le note. */
  children?: ReactNode;
};

// Un gruppo di scelte che si escludono, con i pallini veri del browser: Tab entra nel gruppo, le frecce
// cambiano scelta. Il nome di ogni pallino è la sola etichetta; la spiegazione arriva come descrizione.
export function RadioGroup<V extends string>({
  name,
  legend,
  hideLegend = false,
  options,
  leading,
  value,
  onChange,
  disabled = false,
  describedBy,
  look = "list",
  className,
  children,
}: Props<V>) {
  return (
    <fieldset
      className={className ? `g-radio ${className}` : "g-radio"}
      data-look={look}
      disabled={disabled}
      aria-describedby={describedBy}
    >
      <legend className={hideLegend ? "g-visually-hidden" : "g-radio__legend"}>{legend}</legend>
      <div className="g-radio__options">
        {leading}
        {options.map((option, index) => {
          const id = `${name}-${index}`;
          const hintId = `${id}-hint`;
          const labelId = `${id}-label`;
          const asideId = `${id}-aside`;
          const described = [option.aside ? asideId : null, option.hint ? hintId : null].filter(Boolean).join(" ");
          const off = disabled || option.disabled === true;
          return (
            <div key={option.value} className="g-radio__option" data-disabled={off ? "true" : undefined}>
              <label className="g-radio__label" htmlFor={id}>
                <input
                  id={id}
                  className="g-radio__input"
                  type="radio"
                  name={name}
                  value={option.value}
                  checked={value === option.value}
                  disabled={off}
                  aria-labelledby={labelId}
                  aria-describedby={described || undefined}
                  onChange={() => onChange(option.value)}
                />
                <span className="g-radio__body">
                  <span id={labelId} className="g-radio__name">{option.label}</span>
                  {option.aside ? <span id={asideId} className="g-radio__aside">{option.aside}</span> : null}
                </span>
              </label>
              {option.hint ? <p id={hintId} className="g-radio__hint">{option.hint}</p> : null}
            </div>
          );
        })}
      </div>
      {children}
    </fieldset>
  );
}
